import { type CampaignListICPOverview, ICPOverview, Snippet } from "./client";
import { type MailboxDTO, type MailboxBounceStats } from "@coldreach/campaigns";

export function useItems() {
    return useAsyncData(
        `items`,
        () => $fetch('/api/items'),
        { server: false, default: () => [] },
    );
}

export function useSequences() {
    return useAsyncData(
        `sequences`,
        async () => {
            return await $fetch('/api/sequences');
        },
        { server: false, default: () => [] as SequenceListItem[] },
    );
}

export function useMailboxes(options?: { includeDraining?: boolean }) {
    const includeDraining = options?.includeDraining ?? true;
    return useAsyncData(
        `mailboxes-${includeDraining ? 'all' : 'selectable'}`,
        async () => {
            return await $fetch('/api/mailboxes', { query: { includeDraining } });
        },
        { server: false, default: () => [] as MailboxDTO[] },
    );
}

export function useMailboxBounceStats() {
    return useAsyncData(
        `mailbox-bounce-stats`,
        async () => {
            return await $fetch('/api/mailboxes/bounce-stats') as Record<number, MailboxBounceStats>;
        },
        { server: false, default: () => ({} as Record<number, MailboxBounceStats>) },
    );
}

export function useMailboxesWithBounceStats(options?: { includeDraining?: boolean }) {
    const { data: mailboxes, refresh, status, error } = useMailboxes(options);
    const { data: bounceStats, error: bounceStatsError } = useMailboxBounceStats();
    const enriched = computed(() =>
        mailboxes.value.map(m => {
            const s = bounceStats.value[m.id];
            if (!s) return m;
            return { ...m, bounceRate: s.totalSequenced > 0 ? s.bounceRate : null, totalSequenced: s.totalSequenced, totalBounced: s.totalBounced };
        }),
    );
    return { data: enriched, refresh, status, error, bounceStatsError };
}

export function useUpdateMailboxForwarding(mailboxId: number, emails: string[]) {
    return $fetch('/api/mailboxes/forwarding', { method: 'PUT', body: {
        mailboxId,
        emails,
    } });
}

export function useUpdateMailboxSignature(mailboxId: number, signature: string | null) {
    return $fetch('/api/mailboxes/signature', { method: 'PUT', body: {
        mailboxId,
        signature,
    } });
}

export function useUpdateMailboxSignaturesBulk(updates: Array<{ mailboxId: number; signature: string | null }>) {
    return $fetch('/api/mailboxes/signature-bulk', { method: 'PUT', body: { updates } });
}

export function useUpdateMailboxOwner(mailboxId: number, userId: number | null) {
    return $fetch('/api/mailboxes/owner', { method: 'PUT', body: {
        mailboxId,
        userId,
    } });
}

export function useUpdateMailboxDraining(mailboxId: number, draining: boolean) {
    return $fetch('/api/mailboxes/draining', { method: 'PUT', body: {
        mailboxId,
        draining,
    } });
}

export function useUpdateMailboxDefaultReplyRecipients(mailboxId: number, cc: string[], bcc: string[]) {
    return $fetch('/api/mailboxes/default-reply-recipients', { method: 'PUT', body: {
        mailboxId,
        cc,
        bcc,
    } });
}

export async function useSequence(sequenceId: number) {
    return await useAsyncData(
        `sequence-${sequenceId}`,
        async () => {
            return await $fetch(`/api/sequences/${sequenceId}`);
        },
        { server: false },
    );
}

export function useOverviewICPs() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return useAsyncData(
        `overview-icps-${timezone}`,
        async () => {
            // @ts-ignore TS2589: Nitro route type resolution is too deep for this endpoint
            return await $fetch<ICPOverview[]>('/api/icps', { query: { timezone } });
        },
        { server: false, default: () => [] as ICPOverview[] },
    );
}

export function useCampaignListICPs(options: { immediate?: boolean; includeLastActivity?: boolean } = {}) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const includeLastActivity = options.includeLastActivity ?? true;

    return useAsyncData(
        `campaign-list-icps-lite-${timezone}${includeLastActivity ? '' : '-without-activity'}`,
        async () => {
            return await $fetch<CampaignListICPOverview[]>('/api/icps/list-lite', { query: { timezone, includeLastActivity } });
        },
        { server: false, immediate: options.immediate ?? true, default: () => [] as CampaignListICPOverview[] },
    );
}

export function useOrganizationCurrentTier() {
    return useAsyncData(
        `organization-current-tier`,
        async () => {
            const org = await $fetch('/api/organization');
            return org.currentTier;
        },
        { server: false, default: () => null as number | null },
    );
}

export function useICP(icpId: number) {
    return useAsyncData(
        `icp-${icpId}`,
        () => $fetch(`/api/icps/${icpId}`),
        { server: false },
    );
}

export function useLazyICP(icpId: number) {
    return useLazyAsyncData(
        `lazy-icp-${icpId}`,
        async () => {
            return await $fetch(`/api/icps/${icpId}`);
        },
        { server: false },
    );
}

export function usePendingApprovalCount(sequenceId: number) {
    return useAsyncData(
        `pending-approval-count-${sequenceId}`,
        async () => {
            return await $fetch(`/api/sequences/${sequenceId}/contacts/count-pending`);
        },
        { default: () => 0 },
    );
}

export function useSequenceContactsCount(sequenceId: number) {
    return useAsyncData(
        `sequence-contacts-count-${sequenceId}`,
        async () => {
            return await $fetch(`/api/sequences/${sequenceId}/contacts/count`, { method: 'POST' });
        },
        { default: () => 0, server: false },
    );
}

export function useSequenceSnippets(sequenceId: number) {
    return useAsyncData(
        `sequence-snippets-${sequenceId}`,
        async () => {
            return await $fetch('/api/snippets', { query: { sequenceId } });
        },
        { default: () => [] as Snippet[], server: false },
    );
}

export function useSequenceSteps(sequenceId: number) {
    return useAsyncData(
        `sequence-steps-${sequenceId}`,
        async () => {
            const sequence = await $fetch(`/api/sequences/${sequenceId}`);
            return sequence.steps;
        },
        { default: () => [] as SequenceStep[], server: false },
    );
}

export function useSequenceCandidatesCount(sequenceId: number, icpId: number) {
    return useAsyncData(
        `sequence-candidates-count-${sequenceId}-${icpId}`,
        async () => {
            return await $fetch(`/api/sequences/${sequenceId}/contacts/count-pending`, { query: {
                icpId: icpId,
            } });
        }, { server: false, default: () => 0 })
}

export async function useICPSequences(icpId: number) {
    return await useAsyncData(
        `icp-sequences-${icpId}`,
        async () => {
            return await $fetch<SequenceListItem[]>('/api/sequences', { query: { icpId } });
        },
        { server: false, default: () => [] as SequenceListItem[] },
    );
}
